import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  TextInput,
  StyleSheet,
  KeyboardAvoidingView,
  ScrollView,
  Platform,
  ActivityIndicator,
} from 'react-native';
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context';
import { useRouter } from 'expo-router';
import { Svg, Path, Circle } from 'react-native-svg';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { profileService } from '@/services/profileService';
import Toast from '@/components/Toast';
import PrimaryButton from '@/components/PrimaryButton';
import PageHeader from '@/components/PageHeader';
import { useKeyboardVisible } from '@/hooks/useKeyboardVisible';

const CHECK_DELAY_MS = 600;

type AvailabilityStatus = 'idle' | 'checking' | 'available' | 'unavailable' | 'invalid';

export default function ChangeUsernameScreen() {
  const router = useRouter();
  const { t } = useLanguage();
  const insets = useSafeAreaInsets();
  const keyboardVisible = useKeyboardVisible();
  const { userProfile, refreshUserData } = useAuth();

  const currentUsername = userProfile?.username || '';
  const [username, setUsername] = useState(currentUsername);
  const [availability, setAvailability] = useState<AvailabilityStatus>('idle');
  const [availabilityReason, setAvailabilityReason] = useState('');
  const [suggestion, setSuggestion] = useState('');
  const [loading, setLoading] = useState(false);
  const [toastVisible, setToastVisible] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState<'success' | 'error' | 'info'>('success');

  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showToast = (message: string, type: 'success' | 'error' | 'info' = 'success') => {
    setToastMessage(message);
    setToastType(type);
    setToastVisible(true);
  };

  const hideToast = () => setToastVisible(false);

  const isUnchanged = username.toLowerCase() === currentUsername.toLowerCase();

  useEffect(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);

    const trimmed = username.trim();

    if (!trimmed) {
      setAvailability('idle');
      setAvailabilityReason('');
      setSuggestion('');
      return;
    }

    if (isUnchanged) {
      setAvailability('available');
      setAvailabilityReason('');
      setSuggestion('');
      return;
    }

    setAvailability('checking');

    debounceTimer.current = setTimeout(async () => {
      const result = await profileService.checkUsernameAvailability(trimmed);
      if (!result.valid) {
        setAvailability('invalid');
        setAvailabilityReason(result.reason || '');
        setSuggestion('');
      } else if (result.available) {
        setAvailability('available');
        setAvailabilityReason('');
        setSuggestion('');
      } else {
        setAvailability('unavailable');
        setAvailabilityReason('');
        setSuggestion(result.suggestion || '');
      }
    }, CHECK_DELAY_MS);

    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, [username]);

  const handleSave = async () => {
    const trimmed = username.trim();

    if (!trimmed) {
      showToast(t('account.changeUsername.errorRequired'), 'error');
      return;
    }

    if (isUnchanged) {
      router.back();
      return;
    }

    if (availability === 'unavailable' || availability === 'invalid') {
      showToast(availabilityReason || t('account.changeUsername.errorGeneric'), 'error');
      return;
    }

    if (availability === 'checking') {
      showToast(t('account.changeUsername.checking'), 'info');
      return;
    }

    setLoading(true);
    try {
      const result = await profileService.updateUsername(trimmed);
      if (!result.success) {
        throw new Error(result.error || 'Failed');
      }
      await refreshUserData();
      // PRD §5.10 — `settings_changed` (`field=username`). Success path only.
      import('@/utils/clarityEvents')
        .then(({ logSettingsChanged }) => logSettingsChanged({ field: 'username' }))
        .catch(() => {});
      showToast(t('account.changeUsername.successMessage'), 'success');
      setTimeout(() => router.back(), 1500);
    } catch (error) {
      console.error('Error updating username:', error);
      showToast(t('account.changeUsername.errorGeneric'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const canSave = availability === 'available' && !loading;

  const renderAvailabilityIndicator = () => {
    if (!username.trim() || availability === 'idle') return null;

    if (availability === 'checking') {
      return (
        <View style={styles.availabilityRow}>
          <ActivityIndicator size="small" color="#A19F97" />
          <Text style={styles.availabilityChecking}>{t('account.changeUsername.checking')}</Text>
        </View>
      );
    }

    if (availability === 'available') {
      return (
        <View style={styles.availabilityRow}>
          <Svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <Circle cx="8" cy="8" r="8" fill="#0fa971" />
            <Path d="M4.5 8L7 10.5L11.5 6" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </Svg>
          <Text style={styles.availabilityAvailable}>{t('account.changeUsername.available')}</Text>
        </View>
      );
    }

    return (
      <View style={styles.availabilityRow}>
        <Svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <Circle cx="8" cy="8" r="8" fill="#E53935" />
          <Path d="M5.5 5.5L10.5 10.5M10.5 5.5L5.5 10.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
        </Svg>
        <View style={styles.availabilityErrorContent}>
          <Text style={styles.availabilityUnavailable}>
            {availability === 'invalid'
              ? availabilityReason
              : t('account.changeUsername.unavailable')}
          </Text>
          {suggestion ? (
            <Text
              style={styles.suggestion}
              onPress={() => setUsername(suggestion)}
            >
              {suggestion}
            </Text>
          ) : null}
        </View>
      </View>
    );
  };

  return (
    <SafeAreaView style={styles.container} edges={['top']}>
      <PageHeader
        title={t('account.changeUsername.title')}
        onBack={() => router.back()}
        backgroundColor="#FFFFFF"
      />

      <KeyboardAvoidingView
        style={styles.keyboardAvoidingView}
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        keyboardVerticalOffset={Platform.OS === 'ios' ? 0 : 20}
      >
        <ScrollView
          style={styles.scrollView}
          contentContainerStyle={styles.scrollViewContent}
          showsVerticalScrollIndicator={false}
          keyboardShouldPersistTaps="handled"
        >
          <View style={styles.content}>
            <Text style={styles.description}>
              {t('account.changeUsername.subtitle')}
            </Text>

            <View style={styles.form}>
              <View style={styles.inputGroup}>
                <Text style={styles.label}>{t('account.changeUsername.label')}</Text>
                <View style={styles.inputWrapper}>
                  <Text style={styles.atSign}>@</Text>
                  <TextInput
                    style={styles.input}
                    value={username}
                    onChangeText={(text) => setUsername(text.toLowerCase().replace(/\s/g, ''))}
                    autoFocus
                    placeholder={t('account.changeUsername.placeholder')}
                    placeholderTextColor="#A19F97"
                    autoCapitalize="none"
                    autoCorrect={false}
                    autoComplete="off"
                    maxLength={30}
                  />
                </View>
                {renderAvailabilityIndicator()}
                <Text style={styles.hint}>{t('account.changeUsername.hint')}</Text>
              </View>
            </View>
          </View>
        </ScrollView>

        <View style={[styles.footer, { paddingBottom: keyboardVisible ? 16 : insets.bottom + 24 }]}>
          <PrimaryButton
            title={loading
              ? t('account.changeUsername.buttonLoading')
              : t('account.changeUsername.button')}
            onPress={handleSave}
            disabled={!canSave || loading}
          />
        </View>
      </KeyboardAvoidingView>

      <Toast
        visible={toastVisible}
        type={toastType}
        message={toastMessage}
        onHide={hideToast}
        duration={4000}
      />
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#FFFFFF',
  },
  keyboardAvoidingView: {
    flex: 1,
  },
  scrollView: {
    flex: 1,
  },
  scrollViewContent: {
    flexGrow: 1,
  },
  content: {
    flex: 1,
    paddingHorizontal: 24,
    paddingTop: 24,
  },
  description: {
    fontSize: 24,
    fontFamily: 'Sentient-Regular',
    color: '#151515',
    lineHeight: 28.8,
    marginBottom: 24,
  },
  currentBlock: {
    backgroundColor: '#F9F8F3',
    borderRadius: 12,
    padding: 16,
    marginBottom: 28,
    gap: 4,
  },
  currentLabel: {
    fontSize: 12,
    fontFamily: 'Inter_400Regular',
    color: '#7e7d75',
    lineHeight: 16,
  },
  currentValue: {
    fontSize: 16,
    fontFamily: 'Inter_500Medium',
    color: '#151515',
  },
  form: {
    gap: 24,
  },
  inputGroup: {
    gap: 8,
  },
  label: {
    color: '#151515',
    fontFamily: 'Inter',
    fontWeight: '700',
    fontSize: 14,
    lineHeight: 14,
  },
  inputWrapper: {
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#E7EAED',
    borderRadius: 12,
    backgroundColor: '#FFFFFF',
    paddingHorizontal: 16,
  },
  atSign: {
    fontSize: 16,
    fontFamily: 'Inter_400Regular',
    color: '#A19F97',
    marginRight: 2,
  },
  input: {
    flex: 1,
    paddingVertical: 12,
    fontSize: 16,
    fontFamily: 'Inter_400Regular',
    color: '#151515',
  },
  availabilityRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 6,
  },
  availabilityErrorContent: {
    flex: 1,
    gap: 2,
  },
  availabilityChecking: {
    fontSize: 13,
    fontFamily: 'Inter_400Regular',
    color: '#A19F97',
  },
  availabilityAvailable: {
    fontSize: 13,
    fontFamily: 'Inter_400Regular',
    color: '#0fa971',
  },
  availabilityUnavailable: {
    fontSize: 13,
    fontFamily: 'Inter_400Regular',
    color: '#E53935',
  },
  suggestion: {
    fontSize: 13,
    fontFamily: 'Inter_500Medium',
    color: '#015480',
    textDecorationLine: 'underline',
  },
  hint: {
    fontSize: 12,
    fontFamily: 'Inter_400Regular',
    color: '#A19F97',
    lineHeight: 16,
  },
  footer: {
    paddingHorizontal: 24,
    paddingTop: 8,
  },
});
